import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Input, Select, message, Alert, Card, Table, Row, Col, Typography, Tag, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { stockAdjustmentService } from '@services/operator/stockAdjustmentService';
import { REASON_CODES } from '@constants/index';

const { Text } = Typography;

// inventoryService 由 caller 提供，各家 service 形狀不一；modal 內部以 unknown 處理 response
interface InventoryService {
  [key: string]: unknown;
}

interface InventoryRecord {
  id: string;
  product_sku?: string;
  product_name?: string;
  inventory_type?: string;
  batch?: string | null;
  expiration_date?: string | null;
  [key: string]: unknown;
}

interface ShelfEntry {
  shelf_id: string;
  shelf_location?: string;
  quantity: number;
  actualQuantity: number | null;
  [key: string]: unknown;
}

interface QuickStockAdjustmentModalProps {
  visible: boolean;
  inventory: InventoryRecord | null;
  inventoryService: InventoryService;
  onCancel: () => void;
  onSuccess?: () => void;
}

const QuickStockAdjustmentModal: React.FC<QuickStockAdjustmentModalProps> = ({ visible, inventory, inventoryService, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loadingShelves, setLoadingShelves] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shelfEntries, setShelfEntries] = useState<ShelfEntry[]>([]);

  useEffect(() => {
    if (visible && inventory) {
      form.resetFields();
      form.setFieldsValue({ reason_code: 'COUNT_ERROR' });
      fetchShelfInventories();
    } else {
      setShelfEntries([]);
    }
  }, [visible, inventory]);

  if (!inventory) return null;

  const fetchShelfInventories = async () => {
    setLoadingShelves(true);
    try {
      const response = await (inventoryService.getShelfInventories as (id: string) => Promise<{ success: boolean; data?: any; message?: string }>)(inventory.id);
      if (response.success) {
        const list = Array.isArray(response.data) ? response.data : (response.data.list || []);
        const entries = list.map((si: any) => ({ ...si, actualQuantity: si.quantity }));
        setShelfEntries(entries);
      }
    } catch (error) {
      console.error(error);
      message.error(t('common.message.fetch_failed'));
    } finally {
      setLoadingShelves(false);
    }
  };

  const handleQuantityChange = (shelfId: string, value: number | null) => {
    setShelfEntries(prev => prev.map(e =>
      e.shelf_id === shelfId ? { ...e, actualQuantity: value } : e
    ));
  };

  const changedEntries = shelfEntries.filter(e => e.actualQuantity != null && e.actualQuantity !== e.quantity);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (changedEntries.length === 0) {
        message.error(t('shared.stock_adjustment.error.no_difference'));
        return;
      }

      setSubmitting(true);
      const payload = {
        note: values.note,
        items: changedEntries.map(e => ({
          inventory_id: inventory.id,
          shelf_id: e.shelf_id,
          quantity: (e.actualQuantity as number) - e.quantity,
          reason_code: values.reason_code,
          note: values.note,
        }))
      };

      const response = await stockAdjustmentService.create(payload);
      if (response.success) {
        message.success(t('common.message.create_success'));
        onCancel();
        onSuccess?.();
      } else {
        message.error(response.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const shelfColumns = [
    {
      title: t('models.shelf.location'),
      dataIndex: 'shelf_location',
      width: 140,
    },
    {
      title: t('shared.stock_adjustment.system_quantity'),
      dataIndex: 'quantity',
      width: 100,
      align: 'right' as const,
    },
    {
      title: t('shared.stock_adjustment.actual_quantity'),
      width: 130,
      align: 'right' as const,
      render: (_: unknown, r: ShelfEntry) => (
        <InputNumber
          min={0}
          value={r.actualQuantity}
          onChange={(val) => handleQuantityChange(r.shelf_id, val as number | null)}
          style={{ width: '100%' }}
          size="small"
        />
      ),
    },
    {
      title: t('shared.stock_adjustment.difference'),
      width: 80,
      align: 'right' as const,
      render: (_: unknown, r: ShelfEntry) => {
        const diff = r.actualQuantity != null ? r.actualQuantity - r.quantity : null;
        if (diff == null || diff === 0) return <Text type="secondary">-</Text>;
        const isGain = diff > 0;
        return (
          <Text strong style={{ color: isGain ? '#52c41a' : '#ff4d4f' }}>
            {isGain ? `+${diff}` : diff}
          </Text>
        );
      },
    },
  ];

  return (
    <Modal
      title={t('shared.stock_adjustment.quick_adjust_title')}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={submitting}
      okText={t('shared.stock_adjustment.quick_adjust_submit')}
      cancelText={t('common.action.cancel')}
      okButtonProps={{ disabled: changedEntries.length === 0 }}
      width={700}
    >
      {/* 商品資訊 */}
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }} bordered={false}>
        <Space>
          <Tag color="blue">{inventory.product_sku}</Tag>
          <Text>{inventory.product_name}</Text>
        </Space>
        <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
          {inventory.batch && <span>{t('models.inventory.batch')}: {inventory.batch}</span>}
          {inventory.expiration_date && <span style={{ marginLeft: 12 }}>{t('models.inventory.expiration_date')}: {inventory.expiration_date}</span>}
          {inventory.inventory_type && <span style={{ marginLeft: 12 }}>{t('models.inventory.inventory_type')}: {inventory.inventory_type}</span>}
        </div>
      </Card>

      {/* 儲位數量表 */}
      {shelfEntries.length === 0 && !loadingShelves ? (
        <Alert
          message={t('shared.stock_adjustment.error.no_shelves')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Table
          columns={shelfColumns}
          dataSource={shelfEntries}
          rowKey="shelf_id"
          loading={loadingShelves}
          pagination={false}
          size="small"
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 原因碼和備註 */}
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label={t('models.stock_adjustment.reason')} name="reason_code" rules={[{ required: true }]}>
              <Select>
                {REASON_CODES.map((code: string) => (
                  <Select.Option key={code} value={code}>{t(`enums.stock_adjustment.reason_code.${code}`)}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('models.stock_adjustment.note')} name="note">
              <Input.TextArea rows={2} placeholder={t('common.placeholder.note')} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {changedEntries.length > 0 && (
        <Alert
          message={t('shared.stock_adjustment.adjustment_summary', { count: changedEntries.length })}
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
}

export default QuickStockAdjustmentModal;
